<template>
  <section class="max-w-screen-sm mx-auto px-4 md:px-0 xl:container xl:pt-10">
    <div
      v-if="show"
      class="w-full flex flex-col space-y-6 xl:flex-row xl:justify-start xl:items-start xl:space-y-0"
    >
      <div class="hidden w-96 xl:block">
        <picture id="desktop-show-image">
          <source :srcset="showDesktopSrcset" sizes="384px" type="image/webp" />
          <img
            :alt="show.name"
            :src="show.image"
            :width="showDesktopCrop?.width"
            :height="showDesktopCrop?.height"
            class="rounded-md"
          />
        </picture>
      </div>
      <div class="xl:shrink-[20] xl:px-12">
        <h1 id="show-title" class="text-3xl font-semibold font-heading">{{ show.name }}</h1>

        <h2
          id="show-date"
          class="uppercase text-base font-semibold mb-3 font-date text-fg-disclaimer"
        >
          {{ dateStr }}
        </h2>

        <div class="flex flex-wrap gap-2 sm:gap-4 mb-4">
          <PerformanceButton
            v-for="performance in performances"
            :key="performance.id"
            :performance="performance"
            :error="error"
          />
        </div>

        <picture id="mobile-show-image" class="xl:hidden">
          <source
            :srcset="showMobileSrcset"
            sizes="(min-width: 768px) 640px, (min-width: 640px) 608px, calc(100vw - 2rem)"
            type="image/webp"
          />
          <img
            :alt="`${show.name} Cover`"
            :src="show.image"
            :width="showMobileCrop?.width"
            :height="showMobileCrop?.height"
            class="w-full"
          />
        </picture>

        <div
          id="show-description"
          class="text-sm md:text-base mt-4 mb-2 font-sans whitespace-pre-wrap"
          v-html="sanitizedDescription"
        />

        <div v-if="error.message">
          {{ error.message }}
        </div>
      </div>
      <template v-if="performanceDates.length > 1">
        <hr class="h-1 w-full bg-surface-base xl:hidden" />
        <div class="xl:ml-auto xl:w-72 xl:pt-28">
          <h2 class="font-heading font-semibold text-lg uppercase mb-4">More Performances</h2>
          <div class="flex flex-col mb-4">
            <router-link
              v-for="performanceDate in performanceDates"
              :key="performanceDate"
              :id="`show-performance-date-${performanceDate}`"
              class="cursor-pointer"
              :class="performanceDate === date ? 'text-fg-g-primary font-semibold' : 'text-primary'"
              :to="{ name: 'showDetails', params: { showId, date: performanceDate } }"
            >
              {{ settingStore.longDate(performanceDate) }}
            </router-link>
          </div>
        </div>
      </template>
    </div>
    <template v-else-if="loading">
      <Spinner></Spinner>
    </template>
    <template v-else>
      Something went wrong, there do not seem to be any matching performances.
    </template>
  </section>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import Spinner from '@/common/components/atoms/Spinner.vue'
import { StorePerformance, StoreShow, buildShowSrcset, cropDimensionsPx } from '@/models'
import PerformanceButton from '@/modules/guestUi/components/organisms/PerformanceButton.vue'
import { usePerformancePaginate } from '@/modules/guestUi/stores/PerformancePaginateStore'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { buildMetaDescription } from '@/utils/metaDescription'
import { buildPageTitle } from '@/utils/pageTitle'
import { sanitizeHtmlForClient } from '@/utils/sanitize'
import { buildEnabledShowRichPagePath } from '@/utils/showRichPagePath'
import { sortBy } from '@/utils/utils'

import { ImageRead } from '@generated/types'

export default defineComponent({
  name: 'ShowDetails',
  data() {
    return {
      loading: true,
      error: {
        code: '',
        message: '',
      },
    }
  },
  async serverPrefetch() {
    await this.loadShowDetails()
  },
  async created() {
    await this.loadShowDetails()
  },
  methods: {
    async loadShowDetails() {
      // ssr-render.js turns this flag into an HTTP 404, and this method also runs client-side, so
      // it has to be cleared on entry rather than left wherever the previous load put it.
      this.settingStore.notFound = false
      this.settingStore.ssrRedirectPath = null
      try {
        if (isNaN(this.showId)) {
          this.loading = false
          this.clearShowHeadTags()
          this.settingStore.notFound = true
          return
        }

        let fetchFailed = false
        try {
          await this.performancePaginateStore.getDetailPerformances({
            start_date: this.routeDate || this.venueToday,
            show: this.showId,
            pagination: 'false',
          })
        } catch {
          fetchFailed = true
        }
        this.loading = false

        if (this.show) {
          this.settingStore.ssrCanonicalPath =
            buildEnabledShowRichPagePath(this.show) ?? `/shows/${this.showId}/${this.date}`
          this.settingStore.ssrTitle = buildPageTitle(
            [this.show.name, this.dateStr],
            this.settingStore,
            { locality: true },
          )
          this.settingStore.ssrImage = this.show.srcset.rectLg.src
          this.settingStore.ssrImageDimensionsPx = cropDimensionsPx(this.show.srcset.rectLg)
          this.settingStore.ssrDescription = buildMetaDescription(this.show.description)
          // Meta ViewContent for the focused performance (no-op on the server).
          this.checkoutStore.captureFacebookViewContentEvent(this.performances[0])
        } else if (this.routeDate || fetchFailed) {
          this.clearShowHeadTags()
          this.settingStore.notFound = true
        } else {
          this.clearShowHeadTags()
          this.settingStore.ssrRedirectPath = this.showListPath
          if (!import.meta.env.SSR) await this.$router.replace(this.showListPath)
        }
      } finally {
        this.settingStore.ssrPrefetchDone = true
      }
    },
    // Clears every field the found branch sets, so a not-found show can't keep the previous
    // one's description or image now that all three description tags render from it.
    clearShowHeadTags() {
      this.settingStore.ssrTitle = this.settingStore.venue_name
      this.settingStore.ssrCanonicalPath = null
      this.settingStore.ssrDescription = null
      this.settingStore.ssrImage = null
      this.settingStore.ssrImageDimensionsPx = null
    },
  },
  computed: {
    ...mapStores(useSettingStore, usePerformancePaginate, useCheckoutStore),
    showId(): number {
      return Number(this.$route.params.showId)
    },
    routeDate(): string {
      // Path param is canonical; query fallback keeps legacy ?date= links working
      // until the proxy 301 redirects them (remove the fallback once that ships,
      // so SSR never reads route.query). Absent optional route params come
      // through as '' (not undefined), so || not ??.
      const raw = this.$route.params.date || this.$route.query.date
      if (!raw) return ''
      const value = raw.toString()
      return value.length > 10 ? value.substring(0, 10) : value
    },
    date(): string {
      return this.routeDate || this.performanceDates.find(date => date >= this.venueToday) || ''
    },
    venueToday(): string {
      return this.settingStore.dateIsoStr(new Date().toISOString())
    },
    showListPath(): string {
      const cut = this.$route.fullPath.search(/[?#]/)
      return cut === -1 ? '/' : `/${this.$route.fullPath.slice(cut)}`
    },
    dateStr(): string {
      return this.settingStore.mediumDate(this.date)
    },
    performances(): StorePerformance[] {
      return sortBy(
        this.performancePaginateStore.detailPerformances.filter(
          performance => performance.show_id == this.showId && performance.date == this.date,
        ),
        'datetime',
      )
    },
    performanceDates(): string[] {
      return [
        ...new Set(
          // Filter before sorting: sortBy mutates, and the store's array must not
          // be reordered from inside a computed getter.
          sortBy(
            this.performancePaginateStore.detailPerformances.filter(
              performance => performance.show_id === this.showId,
            ),
            'date',
          ).map(performance => performance.date),
        ),
      ]
    },
    show(): StoreShow | null {
      if (this.performances.length) {
        return this.performances[0].show
      }
      return null
    },
    sanitizedDescription(): string {
      return sanitizeHtmlForClient(this.show?.description)
    },
    showMobileSrcset(): string {
      return this.show
        ? buildShowSrcset(this.show.srcset, this.settingStore.image_ratio_mobile)
        : ''
    },
    showDesktopSrcset(): string {
      return this.show
        ? buildShowSrcset(this.show.srcset, this.settingStore.image_ratio_desktop)
        : ''
    },
    showMobileCrop(): ImageRead | null {
      return this.settingStore.image_ratio_mobile === 'square'
        ? (this.show?.srcset.sqXl ?? null)
        : (this.show?.srcset.rectLg ?? null)
    },
    showDesktopCrop(): ImageRead | null {
      return this.settingStore.image_ratio_desktop === 'square'
        ? (this.show?.srcset.sqXl ?? null)
        : (this.show?.srcset.rectLg ?? null)
    },
  },
  components: { Spinner, PerformanceButton },
  async mounted() {
    await this.checkoutStore.abandonAndClear()
  },
})
</script>
